import {
  DrawerProps,
  IconButton,
  List,
  Menu,
  MenuList,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import styled from '@emotion/styled';
import { MenuRoute } from '@blb-ventures/react-components';
import { SmartMenu } from '@web/common/components/navigation/smart-menu';
import { SafeAreaDrawer } from './safe-area-drawer';

export const StyledList = styled(List)<{ anchor?: DrawerProps['anchor'] }>`
  ${props => `padding-${props.anchor ?? ''}: env(safe-area-inset-${props.anchor ?? ''}, 0);`}
`;

interface MoreButtonProps {
  hasDivider?: boolean;
  icon?: ReactNode;
  menuItems: MenuRoute[];
  disabled?: boolean;
  anchor?: DrawerProps['anchor'];
}

export const MoreButton: FC<MoreButtonProps> = ({
  icon,
  menuItems,
  disabled,
  anchor = 'bottom',
  hasDivider = true,
}) => {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const handleClick = (e: any) => {
    if (mdUp) {
      setAnchorEl(e.currentTarget);
    } else {
      setDrawerOpen(true);
    }
  };
  const handleMenuClick = useCallback(
    (originalClickHandler: any) => () => {
      if (mdUp) {
        setAnchorEl(null);
      } else {
        setDrawerOpen(false);
      }
      if (originalClickHandler != null) {
        originalClickHandler();
      }
    },
    [mdUp],
  );
  const menu = useMemo(
    () => (
      <SmartMenu
        hasDivider={hasDivider}
        menuItems={menuItems.map(it => ({ ...it, onClick: handleMenuClick(it.onClick) }))}
      />
    ),
    [hasDivider, menuItems, handleMenuClick],
  );
  return (
    <>
      <IconButton onClick={handleClick} disabled={disabled} data-e2e="more-button">
        {icon ?? <MoreVertIcon />}
      </IconButton>
      {mdUp ? (
        <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
          <MenuList dense disablePadding data-e2e="more-button-list">
            {menu}
          </MenuList>
        </Menu>
      ) : (
        <SafeAreaDrawer open={drawerOpen} anchor={anchor} onClose={() => setDrawerOpen(false)}>
          <StyledList anchor={anchor}>{menu}</StyledList>
        </SafeAreaDrawer>
      )}
    </>
  );
};
